import React, { useState, useEffect, useCallback } from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { CreationMode, CreateGuideModalProps } from '../types';
import ModeSelection from './ModeSelection';
import FormHeader from './FormHeader';
import GenerateGuideFromPdf from './GenerateGuideFromPdf';
import { useCreateGuide } from '../../../hooks/useGuides';

const CreateGuideModal: React.FC<CreateGuideModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation('admin');
  const [creationMode, setCreationMode] = useState<CreationMode>(null);
  const { mutateAsync: createGuideMutation, isPending: isSubmitting } = useCreateGuide();

  const handleClose = useCallback(() => {
    setCreationMode(null);
    onClose();
  }, [onClose]);

  const handleModeSelect = (mode: CreationMode) => {
    setCreationMode(mode);
  };

  const handlePdfSubmit = async (fileId: string, processImages: boolean, contentDepth: string) => {
    await createGuideMutation({
      mode: 'pdf',
      fileId,
      processImages,
      contentDepth: contentDepth as 'simplified' | 'technical' | 'in-depth',
    });
    handleClose();
  };

  // Auto-create guide when manual mode is selected
  useEffect(() => {
    if (creationMode === 'manual' && !isSubmitting) {
      const createGuide = async () => {
        await createGuideMutation({ mode: 'manual' });
        handleClose();
      };
      createGuide();
    }
  }, [creationMode, isSubmitting, createGuideMutation, handleClose]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="2xl" placement="center" backdrop="blur" scrollBehavior="inside">
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          {!creationMode && t('guides.modal.title')}
          {creationMode === 'pdf' && t('guides.modal.pdfImport.title')}
          {creationMode === 'manual' && t('guides.modal.manualCreate.title')}
        </ModalHeader>
        <ModalBody>
          {!creationMode && <ModeSelection onModeSelect={handleModeSelect} />}
          {creationMode === 'pdf' && (
            <div className="space-y-6">
              <FormHeader
                title={t('guides.modal.pdfImport.title')}
                subtitle={t('guides.modal.pdfImport.subtitle')}
                onBack={() => setCreationMode(null)}
              />
              <GenerateGuideFromPdf onSubmit={handlePdfSubmit} onCancel={handleClose} isSubmitting={isSubmitting} />
            </div>
          )}
          {creationMode === 'manual' && isSubmitting && (
            <div className="flex items-center justify-center py-8">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('guides.modal.creating', { defaultValue: 'Creating guide...' })}
              </p>
            </div>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default CreateGuideModal;
